import tippy, { type Instance } from "tippy.js";
import "tippy.js/dist/tippy.css";
import type { Action } from "svelte/action";

type TooltipContent = string | null | undefined;

interface TooltipOptions {
  content: TooltipContent;
  /**
   * On touch devices (no hover), render a tappable "?" badge right after the
   * element so the tooltip is reachable without first activating the element.
   * Ignored where hover works, since the normal hover tooltip already shows.
   */
  helpIcon?: boolean;
  /**
   * Whether a tap shows the tooltip on touch devices. Defaults to `!helpIcon`,
   * which suppresses taps exactly for the interactive controls that ask for a
   * badge: their tap should run their action rather than leave behind a
   * tooltip touch devices have no way to dismiss. Pass `false` on an
   * interactive control that has no badge.
   */
  touch?: boolean;
}

export type TooltipParam = TooltipContent | TooltipOptions;

function normalize(param: TooltipParam): TooltipOptions {
  if (param === null || param === undefined || typeof param === "string") {
    return { content: param };
  }
  return param;
}

function touchEnabled(options: TooltipOptions): boolean {
  return options.touch ?? !options.helpIcon;
}

/**
 * Svelte action: a tippy hover tooltip whose content tracks the passed string
 * (or `{ content, helpIcon, touch }`). Empty content disables the tooltip.
 * Typed against `Element` so it also works on SVG nodes.
 *
 * Requires the badge styles from `@chess-hair/javascript-stuff/tooltip.css`.
 */
export const tooltip: Action<Element, TooltipParam> = (node, param) => {
  let options = normalize(param);

  const instance = tippy(node, {
    content: options.content ?? "",
    touch: touchEnabled(options),
  });
  if (!options.content) instance.disable();

  // Only touch/no-hover devices get the "?" badge; on hover-capable devices the
  // media query never matches so the badge is never created.
  const hoverless =
    typeof matchMedia === "function" ? matchMedia("(hover: none)") : null;

  // The badge and the annotated element are grouped in an inline-flex wrapper so
  // the "?" hugs its own element regardless of the parent's flex gap; the parent
  // then spaces the wrapper as a single unit. Kept null on hover devices.
  let wrapper: HTMLSpanElement | null = null;
  let badge: HTMLButtonElement | null = null;
  let badgeTip: Instance | null = null;

  function stopPropagation(event: Event): void {
    // Keep the tap on the badge from reaching the element it annotates (or any
    // surrounding click handler); tippy's own toggle still fires.
    event.stopPropagation();
    event.preventDefault();
  }

  function mountBadge(): void {
    wrapper = document.createElement("span");
    wrapper.className = "tooltip-help-wrap";
    node.replaceWith(wrapper);
    wrapper.appendChild(node);

    badge = document.createElement("button");
    badge.type = "button";
    badge.className = "tooltip-help";
    badge.textContent = "?";
    badge.addEventListener("click", stopPropagation);
    wrapper.appendChild(badge);

    badgeTip = tippy(badge, { content: options.content ?? "", trigger: "click" });
  }

  function unmountBadge(): void {
    badgeTip?.destroy();
    badgeTip = null;
    if (wrapper) {
      if (node.parentNode === wrapper) {
        // Restore the element to the wrapper's slot so the DOM matches what
        // Svelte expects to tear down.
        wrapper.replaceWith(node);
      } else {
        // Svelte tears down action nodes before calling destroy, so on unmount
        // the element is already gone from the wrapper. Replacing the wrapper
        // with it would resurrect a dead node into the document — just drop
        // the wrapper (and its "?" badge).
        wrapper.remove();
      }
    }
    wrapper = null;
    badge = null;
  }

  function syncBadge(): void {
    const wanted = !!options.helpIcon && !!options.content && !!hoverless?.matches;

    if (wanted && !wrapper) mountBadge();
    else if (!wanted && wrapper) unmountBadge();

    if (badge) {
      badge.setAttribute("aria-label", options.content ?? "");
      badgeTip?.setProps({ content: options.content ?? "" });
    }
  }

  syncBadge();
  hoverless?.addEventListener("change", syncBadge);

  return {
    update(next: TooltipParam) {
      options = normalize(next);
      instance.setProps({
        content: options.content ?? "",
        touch: touchEnabled(options),
      });
      if (options.content) instance.enable();
      else instance.disable();
      syncBadge();
    },
    destroy() {
      instance.destroy();
      hoverless?.removeEventListener("change", syncBadge);
      unmountBadge();
    },
  };
};
